import {
  MailOutlined,
  MobileOutlined,
  SlidersOutlined
} from "@ant-design/icons"
import { Button, Select, Skeleton, Tooltip } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { ContactAvatar, StatusDot } from "../hub/ui.tsx"
import { formatUsPhone } from "../settings/format-phone.ts"
import type { Channel } from "../types/compose.types.ts"
import { useContactConsent } from "./consent-store.ts"
import { type ChannelSummary, summarizeChannel } from "./consent-summary.ts"
import { type CommContact, contactKey } from "./record-contacts.ts"

const CHANNEL_ICON: Record<Channel, React.ReactNode> = {
  email: <MailOutlined aria-hidden />,
  sms: <MobileOutlined aria-hidden />
}

export const ChannelChips = ({ summary }: { summary: ChannelSummary }) => {
  const { t } = useTranslation()
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      <StatusDot
        pill
        tone={summary.service.tone}
        label={t(`recordComm.status.${summary.service.key}`)}
      />
      {summary.marketing && (
        <StatusDot
          pill
          tone={summary.marketing.tone}
          label={t(`recordComm.status.${summary.marketing.key}`)}
        />
      )}
    </span>
  )
}

const ChannelCell = ({
  channel,
  summary
}: {
  channel: Channel
  summary: ChannelSummary | null
}) => {
  const { t } = useTranslation()
  return (
    <div className="flex min-w-0 items-start gap-2">
      <span className="mt-0.5 text-base text-slate-500">
        {CHANNEL_ICON[channel]}
      </span>
      <div className="flex min-w-0 flex-col gap-1">
        <span className="text-xs font-semibold text-slate-700">
          {t(`commHub.channel.${channel}`)}
        </span>
        {summary ? (
          <>
            <span className="truncate text-sm text-slate-900">
              {summary.address
                ? channel === "sms"
                  ? formatUsPhone(summary.address)
                  : summary.address
                : t(
                    `recordComm.status.${channel === "sms" ? "noPhone" : "noEmail"}`
                  )}
            </span>
            {summary.address && <ChannelChips summary={summary} />}
          </>
        ) : (
          <Skeleton.Input active size="small" className="!w-40" />
        )}
      </div>
    </div>
  )
}

/**
 * Who this record's messages go to, and what they have said about email
 * and texts: one row above the message history.
 */
export const ContactStrip = ({
  contacts,
  selected,
  onSelect,
  onOpenPreferences
}: {
  contacts: CommContact[]
  selected: CommContact
  onSelect: (contact: CommContact) => void
  onOpenPreferences: () => void
}) => {
  const { t } = useTranslation()
  const { load, reload } = useContactConsent(selected.ref)
  const name = selected.name || t("recordComm.contact.unnamed")
  const summary = (channel: Channel) =>
    load.state === "ready" ? summarizeChannel(load.consent, channel) : null

  return (
    <section
      aria-label={t("recordComm.contact.stripLabel", { name })}
      className="grid grid-cols-1 gap-3 rounded-lg border border-solid border-slate-200 bg-surface px-4 py-3 md:grid-cols-[minmax(180px,1.1fr)_1.4fr_1.4fr_auto] md:items-center"
    >
      <div className="flex min-w-0 items-center gap-3">
        <ContactAvatar name={name} seed={contactKey(selected.ref)} size={40} />
        <div className="flex min-w-0 flex-col">
          {contacts.length > 1 ? (
            <Select
              aria-label={t("recordComm.contact.choose")}
              size="small"
              variant="borderless"
              className="-ml-2 max-w-full font-semibold"
              popupMatchSelectWidth={false}
              value={contactKey(selected.ref)}
              onChange={(key: string) => {
                const next = contacts.find(
                  (contact) => contactKey(contact.ref) === key
                )
                if (next) onSelect(next)
              }}
              options={contacts.map((contact) => ({
                value: contactKey(contact.ref),
                label: contact.name || t("recordComm.contact.unnamed"),
                role: t(`recordComm.contact.role.${contact.role}`)
              }))}
              optionRender={(option) => (
                <span className="flex flex-col">
                  <span>{option.data.label}</span>
                  <span className="text-xs text-slate-500">
                    {option.data.role}
                  </span>
                </span>
              )}
            />
          ) : (
            <span className="truncate font-semibold text-slate-900">
              {name}
            </span>
          )}
          <span className="text-xs text-slate-500">
            {contacts.length > 1
              ? t("recordComm.contact.showingFor", {
                  role: t(`recordComm.contact.role.${selected.role}`)
                })
              : t(`recordComm.contact.role.${selected.role}`)}
          </span>
        </div>
      </div>
      {load.state === "forbidden" ? (
        <p className="m-0 text-sm text-slate-600 md:col-span-2">
          {t("recordComm.contact.noAccess")}
        </p>
      ) : load.state === "failed" ? (
        <p className="m-0 flex items-center gap-2 text-sm text-slate-600 md:col-span-2">
          {t("recordComm.contact.loadFailed")}
          <Button size="small" onClick={reload}>
            {t("commHub.common.retry")}
          </Button>
        </p>
      ) : (
        <>
          <ChannelCell channel="email" summary={summary("email")} />
          <ChannelCell channel="sms" summary={summary("sms")} />
        </>
      )}
      <Tooltip title={t("recordComm.contact.preferencesHint")}>
        <Button
          type="link"
          icon={<SlidersOutlined aria-hidden />}
          className="justify-self-start px-0 md:justify-self-end"
          onClick={onOpenPreferences}
          disabled={load.state === "forbidden"}
        >
          {t("recordComm.contact.preferences")}
        </Button>
      </Tooltip>
    </section>
  )
}
